import { ApolloCache } from '@apollo/client';
import { cache as apolloCache } from 'lane-shared/apollo';

import {
  ExternalReference,
  GetPropertyExternalReferencesDocument,
} from 'graphql-query-contracts';

export class ExternalReferenceCacheHandler {
  private cache: ApolloCache<unknown>;
  private variables: { propertyId: string };

  constructor(propertyId: string, cache: ApolloCache<unknown> = apolloCache) {
    this.variables = {
      propertyId,
    };

    this.cache = cache;
  }

  removeReferences(references: ExternalReference[]) {
    const existingExternalReferences = this.readReferences();

    if (!existingExternalReferences.length) {
      return;
    }

    const filteredReferences = existingExternalReferences.filter(
      (existingRef: ExternalReference) =>
        !references.some(ref => this.referencesEqual(ref, existingRef))
    );

    this.writeReferences(filteredReferences);
  }

  addReferences(references: ExternalReference[]) {
    const existingExternalReferences = this.readReferences();

    const newReferences = references.filter(
      ref =>
        !existingExternalReferences.some((existingRef: ExternalReference) =>
          this.referencesEqual(ref, existingRef)
        )
    );

    const updatedReferences = [...existingExternalReferences, ...newReferences];

    this.writeReferences(updatedReferences);
  }

  getReferences(): ExternalReference[] {
    const externalReferences = this.readReferences();

    return externalReferences || [];
  }

  private readReferences() {
    const result = this.cache.readQuery({
      query: GetPropertyExternalReferencesDocument,
      variables: this.variables,
    });

    return result?.property?.externalReferences || [];
  }

  private writeReferences(externalReferences: ExternalReference[]) {
    const data = this.cache.readQuery({
      query: GetPropertyExternalReferencesDocument,
      variables: this.variables,
    });

    if (!data?.property) return;

    this.cache.writeQuery({
      query: GetPropertyExternalReferencesDocument,
      variables: this.variables,
      data: {
        ...data,
        property: {
          ...data.property,
          externalReferences,
        },
      },
    });
  }

  private referencesEqual(a: ExternalReference, b: ExternalReference): boolean {
    return (
      a.source.sourceEntityId === b.source.sourceEntityId &&
      a.source.sourceEntityType === b.source.sourceEntityType &&
      a.source.sourceType === b.source.sourceType
    );
  }
}
